/**
 * Anemoia about (custom).
 *
 * The About page is a "dive into the monitor" scroll experience: the visitor
 * starts in the room (the solarmonitor / lunarmonitor world video), and as they
 * scroll the camera flies into the monitor's screen until it fills the viewport
 * and becomes the page — then the Anemoia story scrolls inside it.
 *
 * Mechanics: a tall <section class="about__dive"> pins a 100vh stage; scroll
 * progress p (0..1) through that section drives, every frame:
 *   - the shared world layer scaling up toward the screen's focal point (the
 *     real "flying in"), with a touch of blur near the end;
 *   - a screen panel growing from a small rectangle at the focal point to full
 *     bleed, its boot text typing in;
 *   - the intro headline easing up and out.
 * After the dive the story (.about__page) flows normally over the zoomed world.
 *
 * Enhancement only: with no JS, reduced motion, or on narrow screens, the CSS
 * shows a clean static intro + readable story instead.
 */

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
const lerp = (a, b, t) => a + (b - a) * t;
/** Smoothstep easing, clamped to [edge0, edge1]. */
const smooth = (p, edge0, edge1) => {
  const t = clamp((p - edge0) / (edge1 - edge0), 0, 1);
  return t * t * (3 - 2 * t);
};

/**
 * Original WebGL CRT curvature pass. Draws a transparent-black full-screen
 * overlay whose sampling space is barrel-curved, so the scanlines, aperture
 * grille, vignette and rolling band all bow like a real tube and the corners
 * fall outside into a curved black bezel — selling the bulge while the text
 * underneath stays crisp. Returns null when WebGL is unavailable (CSS CRT then
 * stands in). One full-screen triangle + a cheap shader at capped DPR, paused on
 * hidden tabs, so the GPU cost is minimal.
 * @param {HTMLCanvasElement} canvas
 */
function createCrtPass(canvas) {
  const gl = canvas.getContext('webgl', { alpha: true, premultipliedAlpha: false, antialias: false });
  if (!gl) return null;

  const VS = 'attribute vec2 p; void main(){ gl_Position = vec4(p, 0.0, 1.0); }';
  const FS = `
    precision mediump float;
    uniform vec2 u_res;
    uniform float u_time;
    void main() {
      vec2 uv = gl_FragCoord.xy / u_res;
      vec2 c = uv - 0.5;
      // Strong barrel-curve of the sampling space so the scanlines and the
      // rounded edge darkening bow like a real tube.
      vec2 cuv = uv + c * dot(c, c) * 0.28;
      float lines = 0.5 + 0.5 * cos(cuv.y * u_res.y * 3.14159);
      float scan = mix(0.82, 1.0, lines);                    // visible curved scanlines
      float grille = mix(0.92, 1.0, 0.5 + 0.5 * cos(gl_FragCoord.x * 2.094));
      // Rounded-rectangle vignette in the CURVED space: the corners read as the
      // bulged tube edge and darken hard, but it's a soft GRADIENT (no hard black
      // bezel) that fades to clear in the centre, so the screen still fills the
      // whole window.
      vec2 q = abs(cuv - 0.5) - 0.498;
      float sdf = length(max(q, vec2(0.0))) + min(max(q.x, q.y), 0.0);
      float edge = smoothstep(-0.02, 0.04, sdf);
      float vig = 1.0 - edge * 0.8;
      // Soft curved glare across the top, like light on glass.
      float glare = smoothstep(0.5, 0.0, distance(cuv, vec2(0.5, 0.12))) * 0.05;
      float band = smoothstep(0.07, 0.0, abs(fract(cuv.y - u_time * 0.05) - 0.5));
      float lum = scan * grille * vig + glare;
      float a = clamp(max(1.0 - lum, 0.0) - band * 0.05, 0.0, 1.0);
      gl_FragColor = vec4(0.0, 0.0, 0.0, a);
    }`;

  const compile = (type, src) => {
    const s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
      console.warn('Anemoia CRT shader failed', gl.getShaderInfoLog(s));
      return null;
    }
    return s;
  };

  const vs = compile(gl.VERTEX_SHADER, VS);
  const fs = compile(gl.FRAGMENT_SHADER, FS);
  if (!vs || !fs) return null;

  const prog = gl.createProgram();
  gl.attachShader(prog, vs);
  gl.attachShader(prog, fs);
  gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return null;
  gl.useProgram(prog);

  const buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
  const loc = gl.getAttribLocation(prog, 'p');
  gl.enableVertexAttribArray(loc);
  gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

  const uRes = gl.getUniformLocation(prog, 'u_res');
  const uTime = gl.getUniformLocation(prog, 'u_time');
  gl.clearColor(0, 0, 0, 0);

  const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
  let raf = null;
  let running = false;
  const t0 = performance.now();

  const resize = () => {
    const w = Math.max(1, Math.floor(canvas.clientWidth * dpr));
    const h = Math.max(1, Math.floor(canvas.clientHeight * dpr));
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
      gl.viewport(0, 0, w, h);
    }
  };

  const frame = (now) => {
    if (!running) return;
    resize();
    gl.uniform2f(uRes, canvas.width, canvas.height);
    gl.uniform1f(uTime, (now - t0) / 1000);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    raf = requestAnimationFrame(frame);
  };

  const start = () => {
    if (running) return;
    running = true;
    raf = requestAnimationFrame(frame);
  };
  const stop = () => {
    running = false;
    if (raf) cancelAnimationFrame(raf);
  };

  const onVis = () => (document.hidden ? stop() : start());
  document.addEventListener('visibilitychange', onVis);

  return { start, stop };
}

class AnemoiaAbout extends HTMLElement {
  connectedCallback() {
    this.intro = this.querySelector('[data-about-intro]');
    this.world = document.querySelector('.anemoia-world');

    this.focalX = Number(this.dataset.focalX) || 50;
    this.focalY = Number(this.dataset.focalY) || 44;
    this.zoom = Number(this.dataset.zoom) || 3.4;

    // Custom pointer + WebGL CRT run in both the live and static layouts.
    this.setupCursor();
    this.setupCRT();

    // Static fallback (reduced motion / no world): no fly-in — the room intro
    // and the story simply stack and scroll.
    const canDive = !reducedMotion.matches && this.world;
    if (!canDive) {
      this.classList.add('about--static');
      return;
    }

    this.isMobile = window.matchMedia('(max-width: 749px)').matches;

    // Mobile centres the monitor on screen via the video crop, so the dive just
    // flies into the middle of the viewport.
    if (this.isMobile) {
      this.focalX = Number(this.dataset.focalXMobile) || 50;
      this.focalY = Number(this.dataset.focalYMobile) || 50;
    }

    // Live: hold on the room, scroll locked, until the visitor enters — then
    // fly into the monitor and reveal the curved screen.
    this.classList.add('about--live');
    this.watchRoom();
    this.state = 'intro';
    this.setAttribute('data-state', 'intro');
    document.documentElement.classList.add('about-lock');
    if (this.world) this.world.style.transformOrigin = `${this.focalX}% ${this.focalY}%`;

    if (this.isMobile) {
      // Mobile: only the explicit "Tap to enter" button starts the dive — never
      // a stray scroll or tap elsewhere.
      this.enterBtn = this.querySelector('[data-about-enter]');
      this.onEnterClick = (event) => {
        event.preventDefault();
        this.enter();
      };
      this.enterBtn?.addEventListener('click', this.onEnterClick);
    } else {
      // Desktop: any key or click (outside the nav / links) flies in.
      this.onKey = (event) => {
        if (event.metaKey || event.ctrlKey || event.altKey) return;
        const el = document.activeElement;
        if (el && /^(input|textarea|select)$/i.test(el.tagName)) return; // let search type
        this.enter();
      };
      this.onPress = (event) => {
        // Don't hijack clicks on the nav / links / buttons.
        if (event.target instanceof Element && event.target.closest('#header-group, header, a, button')) return;
        this.enter();
      };
      window.addEventListener('keydown', this.onKey);
      window.addEventListener('pointerdown', this.onPress);
    }
  }

  /** One-shot fly-in: zoom the world into the monitor, then reveal the screen. */
  enter() {
    if (this.state !== 'intro') return;
    this.state = 'entering';
    this.setAttribute('data-state', 'entering');

    if (this.world) {
      // Keep the fly-in on the compositor: hint the transform, and skip the
      // animated blur on mobile — filter animation repaints the whole video
      // layer every frame and is what makes the dive stutter on phones.
      this.world.style.willChange = 'transform';
      if (this.isMobile) {
        this.world.style.transition = 'transform 1.6s cubic-bezier(0.62, 0, 0.2, 1)';
      } else {
        this.world.style.transition = 'transform 1.6s cubic-bezier(0.62, 0, 0.2, 1), filter 1.6s ease';
        this.world.style.filter = 'blur(3px)';
      }
      this.world.style.transform = `scale(${this.zoom})`;
    }

    this.enterTimer = window.setTimeout(() => {
      this.state = 'inside';
      this.setAttribute('data-state', 'inside');
      if (this.world) this.world.style.willChange = ''; // fly-in done, release the hint
      document.documentElement.classList.remove('about-lock');
      window.scrollTo(0, 0);
    }, 1450);
  }

  disconnectedCallback() {
    if (this.onKey) window.removeEventListener('keydown', this.onKey);
    if (this.onPress) window.removeEventListener('pointerdown', this.onPress);
    if (this.enterBtn && this.onEnterClick) this.enterBtn.removeEventListener('click', this.onEnterClick);
    if (this.onCursorMove) window.removeEventListener('pointermove', this.onCursorMove);
    if (this.cursorEl) this.cursorEl.remove();
    if (this.enterTimer) clearTimeout(this.enterTimer);
    if (this.roomTimer) clearTimeout(this.roomTimer);
    if (this.roomVideoEl && this.onRoomData) {
      this.roomVideoEl.removeEventListener('loadeddata', this.onRoomData);
    }
    if (this.crt) this.crt.stop();
    document.documentElement.classList.remove('about-lock');
    // Hand the world layer back exactly as we found it.
    if (this.world) {
      this.world.style.transition = '';
      this.world.style.transform = '';
      this.world.style.transformOrigin = '';
      this.world.style.filter = '';
      this.world.style.willChange = '';
    }
  }

  /** The world layer for the mode currently on screen, if this page has one. */
  roomEnv() {
    if (!this.world) return null;
    const mode = document.documentElement.getAttribute('data-anemoia-mode') === 'lunar' ? 'lunar' : 'solar';
    return this.world.querySelector(`.anemoia-world__env[data-mode="${mode}"]`);
  }

  /**
   * "Press any key to enter" is painted inside the room video, so when that
   * video is slow (multi-megabyte, and no poster still is configured) or absent
   * the first screen is a bare plate — forced near-black on this page — with
   * nothing on it and no way to know a keypress is what's wanted. Wait a beat;
   * if the room still hasn't painted, put a real prompt on screen, and drop it
   * the moment the room arrives so a fast load never sees it.
   */
  watchRoom() {
    const env = this.roomEnv();
    const video = env && env.querySelector('video');
    /* A poster still counts as the room arriving: the world layer fades one in
       when its loop runs late, and it is a frame of this same scene. Adding the
       pill on top of it would be talking over a screen that is already there.
       Read only the layer actually on screen — the other mode's keeps whatever
       flag it was left with, behind opacity 0. */
    const roomShowing = () =>
      Boolean((video && video.readyState >= 2) || (env && env.dataset.poster === 'on'));

    // No video element at all: the plate is the room and always will be, so the
    // prompt is the permanent state rather than a stand-in.
    if (!video) {
      if (!roomShowing()) this.classList.add('about--roomless');
      return;
    }
    if (video.readyState >= 2) return; // already painted; never flash the prompt

    // Deliberately longer than the world layer's own poster grace, so the still
    // has settled one way or the other before this decides anything.
    this.roomTimer = window.setTimeout(() => {
      if (!roomShowing()) this.classList.add('about--roomless');
    }, 1200);

    this.roomVideoEl = video;
    this.onRoomData = () => {
      window.clearTimeout(this.roomTimer);
      this.roomTimer = null;
      this.classList.remove('about--roomless');
    };
    video.addEventListener('loadeddata', this.onRoomData, { once: true });
  }

  /** adampointer.png follower, matching the other Anemoia pages. */
  setupCursor() {
    if (!this.dataset.pointer || !window.matchMedia('(pointer: fine)').matches) return;

    this.cursorEl = document.createElement('div');
    this.cursorEl.className = 'about__cursor';
    this.cursorEl.setAttribute('aria-hidden', 'true');
    this.appendChild(this.cursorEl);
    this.classList.add('about--cursor');

    this.onCursorMove = (event) => {
      this.cursorEl.style.left = `${event.clientX}px`;
      this.cursorEl.style.top = `${event.clientY}px`;
      this.cursorEl.classList.add('is-visible');
      const overLink = event.target instanceof Element && event.target.closest('a, button');
      this.cursorEl.classList.toggle('is-over', Boolean(overLink));
    };
    window.addEventListener('pointermove', this.onCursorMove, { passive: true });
  }

  /** Optional WebGL CRT curvature pass (progressive enhancement). */
  setupCRT() {
    if (this.dataset.webglCrt !== '1' || reducedMotion.matches) return;
    const canvas = this.querySelector('[data-about-crt-canvas]');
    const overlay = this.querySelector('[data-about-crt]');
    if (!canvas || !overlay) return;
    const crt = createCrtPass(canvas);
    if (!crt) return; // WebGL unavailable — the CSS CRT stays as fallback
    this.crt = crt;
    overlay.classList.add('gl-on');
    crt.start();
  }
}

if (!customElements.get('anemoia-about')) {
  customElements.define('anemoia-about', AnemoiaAbout);
}
